import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import CreditService from '../services/creditService';
import { supabase } from '../lib/supabase';

const CreditPackCard = ({ pack, onPurchaseSuccess, onPurchaseError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePurchase = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('Initiating Stripe checkout for:', pack.name);
      
      // Call our Supabase Edge Function to create a checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          packId: pack.id,
          userId: pack.userId // This should be passed from the parent component
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to create checkout session');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe checkout
      window.location.href = data.url;

    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.message || 'Purchase failed');
      onPurchaseError(err.message || 'Purchase failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow duration-200">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon name="Coins" size={24} className="text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-1">{pack.name}</h3>
        <p className="text-2xl font-bold text-primary mb-2">
          ${(pack.amount_cents / 100).toFixed(2)}
        </p>
        <p className="text-sm text-muted-foreground">
          {pack.credits} assessment{pack.credits !== 1 ? 's' : ''}
        </p>
      </div>

      {error && (
        <div className="text-sm text-error bg-error/10 border border-error/20 rounded-lg p-3 mb-4">
          {error}
        </div>
      )}

      <Button
        onClick={handlePurchase}
        disabled={isLoading}
        className="w-full"
        variant="default"
      >
        {isLoading ? (
          <>
            <Icon name="Loader2" size={16} className="animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <Icon name="CreditCard" size={16} className="mr-2" />
            Buy {pack.credits} Credit{pack.credits !== 1 ? 's' : ''}
          </>
        )}
      </Button>

      {/* Premium Upsell */}
      {pack.credits >= 3 && (
        <div className="mt-4 p-3 bg-warning/5 border border-warning/20 rounded-lg">
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="Crown" size={16} className="text-warning" />
            <span className="text-warning font-medium">Premium Tip:</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Need more than {pack.credits} assessments? Premium includes 3 credits/month plus unlimited regenerations for just $19.99/month.
          </p>
        </div>
      )}
    </div>
  );
};

const CreditPackPurchase = ({ userId, onPurchaseSuccess, onPurchaseError }) => {
  const [creditPacks, setCreditPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCreditPacks();
  }, []);

  const loadCreditPacks = async () => {
    try {
      const result = await CreditService.getCreditPacks();
      if (result.success) {
        setCreditPacks(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load credit packs');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = (pack, paymentIntent) => {
    onPurchaseSuccess?.(pack, paymentIntent);
  };

  const handlePurchaseError = (error) => {
    onPurchaseError?.(error);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Icon name="Loader2" size={24} className="animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading credit packs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <Icon name="AlertCircle" size={24} className="text-error mx-auto mb-2" />
        <p className="text-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-muted-foreground">
          Buy credits to take additional career assessments beyond your free tier
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {creditPacks.map((pack) => (
          <CreditPackCard
            key={pack.id}
            pack={{...pack, userId}}
            onPurchaseSuccess={handlePurchaseSuccess}
            onPurchaseError={handlePurchaseError}
          />
        ))}
      </div>

      {/* Premium Comparison */}
      <div className="bg-gradient-to-r from-warning/10 to-primary/10 border border-warning/20 rounded-lg p-6">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <Icon name="Crown" size={24} className="text-warning" />
            <h3 className="text-lg font-semibold text-foreground">Consider Premium Instead</h3>
          </div>
          <p className="text-muted-foreground mb-4">
            If you need more than 3 assessments per month, Premium is the better value:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={16} className="text-success" />
              <span>3 credits included monthly</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={16} className="text-success" />
              <span>Unlimited regenerations</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={16} className="text-success" />
              <span>All premium features</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPackPurchase;